// components/forms/ExpertRegistrationForm.jsx
"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import { Progress } from "@/components/ui/progress";
import { expertFormSchema } from "@/lib/validation-scheme";
import { FormStageNavigation } from "./FormStageNavigation";

const STAGES = [
  { id: 1, title: "Personal Information", description: "Your basic details" },
  { id: 2, title: "Education & Expertise", description: "Your qualifications" },
  { id: 3, title: "Professional Details", description: "Your work experience" },
  { id: 4, title: "Additional Information", description: "Final details" },
];

export function ExpertRegistrationForm({ formData, onSubmit, isSubmitting }) {
  const [currentStage, setCurrentStage] = useState(1);
  const [cvFile, setCvFile] = useState(null);

  const form = useForm({
    resolver: zodResolver(expertFormSchema),
    defaultValues: {
      userType: "expert", // 👈 add this
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      gender: "",
      country: "",
      city: "",
      highestDegree: "",
      fieldOfStudy: "",
      expertise: [],
      skills: [],
      arabicProficiency: "",
      englishProficiency: "",
      digitalSkills: "",
      experienceYears: "",
      dailyRate: "",
      currency: "",
      canTravel: "",
      travelDetails: "",
      specificSkills: "",
      specificExpertise: "",
      termsAccepted: false,
    },
  });

  const watchExpertise = form.watch("expertise");
  const watchSkills = form.watch("skills");
  const watchCanTravel = form.watch("canTravel");

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCvFile(file);
      form.setValue("cv", file);
    }
  };

  const nextStage = async () => {
    const fieldsToValidate = getStageFields(currentStage);
    const isValid = await form.trigger(fieldsToValidate);

    if (isValid) {
      setCurrentStage((prev) => Math.min(prev + 1, STAGES.length));
    }
  };

  const previousStage = () => {
    setCurrentStage((prev) => Math.max(prev - 1, 1));
  };

  const getStageFields = (stage) => {
    switch (stage) {
      case 1:
        return [
          "firstName",
          "lastName",
          "email",
          "phone",
          "gender",
          "country",
          "city",
        ];
      case 2:
        return ["highestDegree", "fieldOfStudy", "expertise", "skills"];
      case 3:
        return [
          "arabicProficiency",
          "englishProficiency",
          "digitalSkills",
          "experienceYears",
        ];
      case 4:
        return ["dailyRate", "currency", "canTravel", "termsAccepted"];
      default:
        return [];
    }
  };

  const handleFormSubmit = async (data) => {
    if (cvFile) {
      data.cv = cvFile;
    }
    await onSubmit(data);
  };

  const progress = (currentStage / STAGES.length) * 100;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-8"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {STAGES[currentStage - 1]?.title}
            </h3>
            <span className="text-sm text-gray-500">
              Step {currentStage} of {STAGES.length}
            </span>
          </div>

          <Progress value={progress} className="h-2" />

          <p className="text-sm text-gray-600">
            {STAGES[currentStage - 1]?.description}
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {currentStage === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.Gender?.map((g) => (
                            <SelectItem key={g.id} value={g.id.toString()}>
                              {g.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.Counties.map((c) => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.Cities.map((c) => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {currentStage === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="highestDegree"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Highest Educational Degree *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select degree" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.education.map((edu) => (
                            <SelectItem key={edu.id} value={edu.id.toString()}>
                              {edu.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fieldOfStudy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Field of Study *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.educationMaj.map((field) => (
                            <SelectItem
                              key={field.id}
                              value={field.id.toString()}
                            >
                              {field.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="expertise"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Areas of Expertise (Select up to 3) *
                      </FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={formData?.ExpField.map((exp) => ({
                            value: exp.id.toString(),
                            label: exp.name_en,
                          }))}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder="Select expertise areas"
                          maxSelected={3}
                        />
                      </FormControl>
                      {watchExpertise?.includes("25") && (
                        <FormField
                          control={form.control}
                          name="specificExpertise"
                          render={({ field: specField }) => (
                            <FormItem className="mt-2">
                              <FormLabel>
                                Please specify other expertise
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...specField}
                                  placeholder="Enter specific expertise"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="skills"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Professional Skills (Select up to 3) *
                      </FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={formData?.WorkSkills.map((skill) => ({
                            value: skill.id.toString(),
                            label: skill.name_en,
                          }))}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder="Select skills"
                          maxSelected={3}
                        />
                      </FormControl>
                      {watchSkills?.includes("21") && (
                        <FormField
                          control={form.control}
                          name="specificSkills"
                          render={({ field: specField }) => (
                            <FormItem className="mt-2">
                              <FormLabel>Please specify other skills</FormLabel>
                              <FormControl>
                                <Input
                                  {...specField}
                                  placeholder="Enter specific skills"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Add more stages similarly */}

            {currentStage === 4 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="dailyRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Daily Rate *</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" min="0" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {formData?.Currincies.map((c) => (
                              <SelectItem key={c.id} value={c.id.toString()}>
                                {c.name_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="canTravel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Are you available for travel? *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select option" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formData?.Travel.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {watchCanTravel === "3" && (
                        <FormField
                          control={form.control}
                          name="travelDetails"
                          render={({ field: travelField }) => (
                            <FormItem className="mt-2">
                              <FormLabel>
                                Please specify travel conditions
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  {...travelField}
                                  placeholder="Enter travel conditions"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <FormLabel>Upload CV/Resume *</FormLabel>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  {cvFile && (
                    <p className="text-sm text-green-600">
                      ✓ File selected: {cvFile.name}
                    </p>
                  )}
                  <FormDescription>
                    Upload your CV (PDF or Word, max 5MB)
                  </FormDescription>
                </div>

                <FormField
                  control={form.control}
                  name="termsAccepted"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          I agree to the terms and conditions
                        </FormLabel>
                        <FormDescription>
                          By registering, you agree to our{" "}
                          <a
                            href="/terms"
                            className="text-primary hover:underline"
                          >
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a
                            href="/privacy"
                            className="text-primary hover:underline"
                          >
                            Privacy Policy
                          </a>
                        </FormDescription>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormStageNavigation
              currentStage={currentStage}
              totalStages={STAGES.length}
              onPrevious={previousStage}
              onNext={nextStage}
              onSubmit={form.handleSubmit(handleFormSubmit)}
              isSubmitting={isSubmitting}
            />
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}
